/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const PREVIEW_SWAP_EXACT_IN_DISCRIMINATOR = new Uint8Array([
  50, 130, 31, 69, 147, 58, 222, 178,
]);

export function getPreviewSwapExactInDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    PREVIEW_SWAP_EXACT_IN_DISCRIMINATOR,
  );
}

export type PreviewSwapExactInInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? ReadonlyAccount<TAccountPool>
        : TAccountPool,
      ...TRemainingAccounts,
    ]
  >;

export type PreviewSwapExactInInstructionData = {
  discriminator: ReadonlyUint8Array;
  amountIn: bigint;
  tradeDirection: number;
};

export type PreviewSwapExactInInstructionDataArgs = {
  amountIn: number | bigint;
  tradeDirection: number;
};

export function getPreviewSwapExactInInstructionDataEncoder(): FixedSizeEncoder<PreviewSwapExactInInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['amountIn', getU64Encoder()],
      ['tradeDirection', getU8Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: PREVIEW_SWAP_EXACT_IN_DISCRIMINATOR,
    }),
  );
}

export function getPreviewSwapExactInInstructionDataDecoder(): FixedSizeDecoder<PreviewSwapExactInInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['amountIn', getU64Decoder()],
    ['tradeDirection', getU8Decoder()],
  ]);
}

export function getPreviewSwapExactInInstructionDataCodec(): FixedSizeCodec<
  PreviewSwapExactInInstructionDataArgs,
  PreviewSwapExactInInstructionData
> {
  return combineCodec(
    getPreviewSwapExactInInstructionDataEncoder(),
    getPreviewSwapExactInInstructionDataDecoder(),
  );
}

export type PreviewSwapExactInInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  amountIn: PreviewSwapExactInInstructionDataArgs['amountIn'];
  tradeDirection: PreviewSwapExactInInstructionDataArgs['tradeDirection'];
};

export function getPreviewSwapExactInInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: PreviewSwapExactInInput<TAccountConfig, TAccountPool>,
  config?: { programAddress?: TProgramAddress },
): PreviewSwapExactInInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
    ],
    data: getPreviewSwapExactInInstructionDataEncoder().encode(
      args as PreviewSwapExactInInstructionDataArgs,
    ),
    programAddress,
  } as PreviewSwapExactInInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool
  >);
}

export type ParsedPreviewSwapExactInInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
  };
  data: PreviewSwapExactInInstructionData;
};

export function parsePreviewSwapExactInInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedPreviewSwapExactInInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { config: getNextAccount(), pool: getNextAccount() },
    data: getPreviewSwapExactInInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
